/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { gnoment } from 'shared-ui/gnoment';
import type { TreePermitData } from '../../types/permit';

export const EASTERN_TIME_ZONE = 'America/New_York';

const PACIFIC = 'America/Los_Angeles';
const MOUNTAIN = 'America/Denver';
const CENTRAL = 'America/Chicago';
const BOISE = 'America/Boise';

const SALE_DATE_TIME_FORMAT = 'MMMM Do [at] h:mm A z';
const SALE_TIME_FORMAT = 'h:mm A z';

export const STATE_TIME_ZONES: Record<string, string> = {
    AK: 'America/Anchorage',
    AL: CENTRAL,
    AR: CENTRAL,
    AZ: 'America/Phoenix',
    CA: PACIFIC,
    CO: MOUNTAIN,
    HI: 'Pacific/Honolulu',
    IA: CENTRAL,
    ID: BOISE,
    IL: CENTRAL,
    KS: CENTRAL,
    LA: CENTRAL,
    MN: CENTRAL,
    MO: CENTRAL,
    MS: CENTRAL,
    MT: MOUNTAIN,
    ND: CENTRAL,
    NE: CENTRAL,
    NM: MOUNTAIN,
    NV: PACIFIC,
    OK: CENTRAL,
    OR: PACIFIC,
    SD: CENTRAL,
    TX: CENTRAL,
    UT: MOUNTAIN,
    WA: PACIFIC,
    WI: CENTRAL,
    WY: MOUNTAIN,
};

// Facilities whose time zone differs from their state's default.
export const FACILITY_TIME_ZONES: Record<string, string> = {
    '06470967-e7d1-11ea-8710-eabd4664df5f': PACIFIC, // Idaho Panhandle National Forest
    '0422847b-e7d4-11ea-8710-eabd4664df5f': PACIFIC, // Nez Perce Clearwater National Forest
    'c1518c31-ec5a-11ea-b83f-6e0e43d29f74': CENTRAL, // Ottawa National Forest (MI)
    '6e07e436-e705-11ea-a244-5e6d4b9f6262': MOUNTAIN, // Nebraska National Forests and Grasslands
    '15652ca3-e649-11ea-a244-5e6d4b9f6262': MOUNTAIN, // Black Hills National Forest (SD)
};

export const getFacilityTimeZone = (
    facility?: Partial<Pick<TreePermitData, 'id' | 'state'>>
): string => {
    const facilityOverride = facility?.id && FACILITY_TIME_ZONES[facility.id];
    if (facilityOverride) {
        return facilityOverride;
    }

    const state = facility?.state?.trim().toUpperCase() ?? '';
    return STATE_TIME_ZONES[state] ?? EASTERN_TIME_ZONE;
};

export const formatOnSaleTime = (onSale: string, facilityTimeZone: string): string => {
    const saleTime = gnoment(onSale);
    const inEastern = (format: string) => saleTime.format(format, EASTERN_TIME_ZONE);
    const inFacility = (format: string) => saleTime.format(format, facilityTimeZone);
    const easternLabel = inEastern(SALE_DATE_TIME_FORMAT);

    if (inFacility('z') === inEastern('z')) {
        return easternLabel;
    }

    const isSameDay = inFacility('YYYY-MM-DD') === inEastern('YYYY-MM-DD');
    const localLabel = inFacility(isSameDay ? SALE_TIME_FORMAT : SALE_DATE_TIME_FORMAT);

    return `${easternLabel} (${localLabel})`;
};
